/**
 * Freight billing audit — GET /api/admin/freight-audit/report.
 * Types mirror services/freight_audit/models.py.
 */
import api from './api';

export type FreightAuditBucket =
  | 'preset_from_portal'
  | 'keyed_at_scan'
  | 'auto_quoted_at_scan'
  | 'keyed_on_invoice'
  | 'no_freight_waived'
  | 'no_freight_collect_or_third_party'
  | 'no_freight_will_call'
  | 'no_freight_no_shipment'
  | 'no_freight_company_vehicle'
  | 'no_freight_unexplained';

export type SourceState = 'ok' | 'unreachable' | 'not_granted' | 'not_applicable';

export interface SalesSiteOrderFacts {
  ship_via: string | null;
  actual_freight_cost: string | null;
  fuel_surcharge_cost: string | null;
}

export interface InvoiceQueueFacts {
  scanned_at: string | null;
  real_freight_amount: string | null;
  real_fuel_amount: string | null;
  status: string | null;
}

export interface FreightAuditRow {
  sales_order_no: string;
  invoice_no: string;
  invoice_date: string;
  invoice_updated: string | null;
  invoice_freight: string;
  customer_no: string | null;
  customer_name: string | null;
  customer_po_no: string | null;
  carrier: string | null;
  order_id: string | null;
  scan_at: string | null;
  preset_created_at: string | null;
  preset_amount: string | null;
  push_applied: boolean;
  charge_freight: boolean | null;
  udf_bl: string | null;
  freight_error_status: string | null;
  sales_site: SalesSiteOrderFacts | null;
  invoice_queue: InvoiceQueueFacts | null;
  sales_site_state: SourceState;
  invoice_queue_state: SourceState;
  /** Carrier is on the truckload / logistics list (no live rate; freight keyed by hand). */
  is_truckload: boolean;
  /** CSR re-key resolved to the AU order that shipped; facts above are that order's. */
  rebill_of: string | null;
  rebill_evidence: 'customer_and_po' | 'customer' | null;
  bucket: FreightAuditBucket;
  confidence: 'exact' | 'approx';
  basis: string;
  edited_after_invoice: boolean;
}

export interface FreightAuditSources {
  invoice_mirror_synced_at: string | null;
  sales_site: SourceState;
  invoice_queue: SourceState;
  detail: string | null;
}

export interface FreightAuditResponse {
  date_from: string;
  date_to: string;
  rows: FreightAuditRow[];
  total: number;
  /** Truckload rows in the window, before filters. */
  truckload_total: number;
  counts: Record<FreightAuditBucket, number>;
  sources: FreightAuditSources;
  caveats: string[];
}

export interface FreightAuditQuery {
  date_from: string;
  date_to: string;
  carrier?: string;
  bucket?: FreightAuditBucket;
  /** true = truckload carriers only, false = everything else, omitted = all. */
  truckload?: boolean;
}

const BASE = '/admin/freight-audit/report';

export const freightAuditApi = {
  report: async (params: FreightAuditQuery): Promise<FreightAuditResponse> => {
    const r = await api.get<FreightAuditResponse>(BASE, { params });
    return r.data;
  },
};
